import { Eye, EyeOff } from 'lucide-react';
import { forwardRef, useId, useState, type InputHTMLAttributes, type ReactNode } from 'react';

import { cn } from '../lib/cn';

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: ReactNode;
  hint?: ReactNode;
  error?: ReactNode;
  /** Visual-only adornment rendered before the input text. */
  leading?: ReactNode;
  /** Visual-only adornment rendered after the input text. */
  trailing?: ReactNode;
  /**
   * For `type="password"`: show an eye button that reveals what was typed.
   * On by default, so every password field can be checked before submitting;
   * pass `false` to turn it off. Ignored when `trailing` is set.
   */
  revealable?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, leading, trailing, revealable = true, id, className, type, ...rest },
  ref,
) {
  const autoId = useId();
  const [revealed, setRevealed] = useState(false);
  const canReveal = type === 'password' && revealable && !trailing;
  const inputId = id ?? autoId;
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-ink text-[13px] font-semibold">
          {label}
        </label>
      )}
      <div
        className={cn(
          'bg-surface flex h-11 items-center gap-2 rounded-xl border px-3.5 transition-all duration-200',
          'focus-within:border-gold-400 focus-within:shadow-focus',
          error
            ? 'border-state-danger focus-within:border-state-danger focus-within:shadow-none'
            : 'border-border-strong/35 hover:border-border-strong/60',
        )}
      >
        {leading && <span className="text-ink-subtle shrink-0">{leading}</span>}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'text-ink placeholder:text-ink-subtle h-full w-full bg-transparent text-sm outline-none',
            className,
          )}
          type={canReveal && revealed ? 'text' : type}
          {...rest}
        />
        {trailing && <span className="text-ink-subtle shrink-0">{trailing}</span>}
        {canReveal && (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            className="text-ink-subtle hover:text-ink grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors"
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            tabIndex={-1}
          >
            {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        )}
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="text-state-danger text-xs font-medium">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-ink-subtle text-xs">
          {hint}
        </p>
      ) : null}
    </div>
  );
});
